import $ from '../../../../../FrontendBundle/Resources/js/jquery';
import { container, Distance, Logger, namespace } from '../../../../../FrontendBundle/Resources/js/ofcs';
import Canvas from '../canvas';
import CanvasDraggable from '../canvas/draggable';
import ContextMenuOverlay from '../canvas/overlay/contextMenuOverlay';
import AddItemCommand from '../command/addItemCommand';
import TablelegItem from '../item/tablelegItem';
import VentilationgrillItem from '../item/ventilationgrillItem';
import RotateHandle from './handle/rotateHandle';
import RotateHandleTabletop from './handle/rotateHandleTabletop';

const defaultOptions = {
    /**
     * Color for the security margin around the bags
     * @cfg {string} [marginColor=#576670]
     */
    marginColor: '#526D7A',

    /**
     * Options for the foam view
     * @cfg {Object} [foamOptions] See FoamView#options
     */
    foamOptions: {},
};

/**
 * Canvas element which draws the editor using svg technology.
 */
class SvgCanvas extends Canvas {
    /**
     * @type {SVGDefsElement}
     */
    defsNode = null;

    /**
     * @type {Tools}
     */
    tools = null;

    /**
     * This event is triggered when the zoom level has changed
     *
     * @event zoomchange
     * @param {Canvas} canvas Me
     * @param {Number} zoom Zoom level
     */

    /**
     * This event is triggered when the pan has changed
     *
     * @event panchange
     * @param {Canvas} canvas Me
     * @param {Number} panX Pan X
     * @param {Number} panY Pan Y
     */

    /**
     * This event is triggered when the tool selection changes
     *
     * @event toolchange
     */

    /**
     * Initializes a new svg canvas instance
     *
     * @constructor
     * @param {number} layoutId Layout ID
     * @param {HTMLElement} wrapper HTML Wrapper Node
     * @param {SVGSVGElement} element SVG Node
     * @param {SVGWrapper} svg SVG Wrapper
     * @param {ContextManager} contextManager Context Manager
     * @param {ItemFactory} itemFactory Item factory
     * @param {PathFactory} pathFactory Path factory
     * @param {History} history History
     * @param {LayerManager} layerManager Layer manager
     * @param {ViewCollection} views View collection
     * @param {SelectionCollection} selection Selection collection
     * @param {Tools} tools Tools
     * @param {CommandExecutor} commandExecutor Global command executor
     * @param {ChainConfigBuilder} itemConfigBuilder Item config builder
     * @param {Object} [options] Hash of config options
     */
    constructor(
        layoutId,
        wrapper,
        element,
        svg,
        contextManager,
        itemFactory,
        pathFactory,
        history,
        layerManager,
        views,
        selection,
        tools,
        commandExecutor,
        itemConfigBuilder,
        options,
    ) {
        super(
            layoutId,
            wrapper,
            contextManager,
            itemFactory,
            history,
            views,
            selection,
            commandExecutor,
            itemConfigBuilder,
            { ...defaultOptions, ...options },
        );

        this.element = $(element);
        this.svg = svg;
        this.defsNode = svg.defs(svg.root());
        this.tools = tools;
        this.pathFactory = pathFactory;
        this.layerManager = layerManager;

        this.layerManager.setCanvas(this);
        this.tools.setCanvas(this);

        /**
         * createitem callback, event is called from create rectangle and circle tool
         */
        this.tools.on('createitem', async (type, config) => {
            const item = await this.itemFactory.create(type, config, this.contextManager.getCurrentItem());
            const command = new AddItemCommand(
                this.contextManager.getCurrentItems(),
                this.selection,
                item,
            );

            this.history.addAndExecuteCommand(command);
            this.selection.selectItem(item, true);
        });

        /**
         * checkselect callback, event is called from select tool
         */
        this.tools.on('checkselect', (bounds) => {
            const selItems = this.findItemsInBounds(bounds);
            this.selection.markItems(selItems, true);
        });

        this.tools.on('stopselect', (bounds) => {
            const selItems = this.findItemsInBounds(bounds);
            this.selection.selectItems(selItems, true);
        });

        this.tools.on('toolchange', (activeTool) => {
            if (activeTool) {
                this.element.css('cursor', activeTool.getCursor());
            }
        });
    }

    initializeInteraction() {
        this.initDragAndDrop();
        this.initContextMenu();
    }

    findItemsInBounds(bounds) {
        const selItems = [];
        this.contextManager.getCurrentItems().each((item) => {
            if (bounds.intersect(item.getAbsoluteBounds()) && item.supportsMultiselection) {
                selItems.push(item);
            }
        }, this);
        return selItems;
    }

    /**
     * Return element
     *
     * @return {SVGSVGElement}
     */
    getElement() {
        return this.element;
    }

    /**
     * Returns instance of svg wrapper
     *
     * @link http://keith-wood.name/svgRef.html
     * @return {SVGWrapper} SVG Wrapper
     */
    getSvgWrapper() {
        return this.svg;
    }

    /**
     * Returns the `<defs>` node from the svg element.
     *
     * @return {SVGDefsElement} Defs node
     */
    getDefsNode() {
        return this.defsNode;
    }

    /**
     *
     * @return {LayerManager}
     */
    getLayerManager() {
        return this.layerManager;
    }

    /**
     * Returns path factory instance.
     *
     * @return {PathFactory} Path Factory
     */
    getPathFactory() {
        return this.pathFactory;
    }

    /**
     * Return tools
     *
     * @return {Tools}
     */
    getTools() {
        return this.tools;
    }

    /**
     * Return contextManager
     *
     * @return {ContextManager}
     */
    getContextManager() {
        return this.contextManager;
    }

    /**
     * Sets the active tool. If the active tool is changed, a #toolchange
     * event is triggered. This event will only be fired if the value of the
     * active tool differs from the given one.
     *
     * @param {string} activeToolName Active tool name
     */
    setActiveTool(activeToolName) {
        this.tools.setActive(activeToolName);
        this.trigger('toolchange');
    }

    /**
     * Returns the current drag mode
     *
     * @return {string} Drag mode key
     */
    getActiveTool() {
        return this.tools.getActive().getKey();
    }

    /**
     * Sets the layout width
     * @param {number} width
     * @return {Editor}
     */
    setWidth(width) {
        this.__parent.setWidth(width);

        if (this.layout) {
            this.layout.attr('width', width);
        }

        return this;
    }

    /**
     * Sets the layout height
     * @param {number} height
     * @return {Editor}
     */
    setHeight(height) {
        this.__parent.setHeight(height);

        if (this.layout) {
            this.layout.attr('height', height);
        }

        return this;
    }

    /**
     * Initialize drag and drop handler.
     *
     * @private
     */
    initDragAndDrop() {
        const dragAndDrop = new CanvasDraggable(this, this.element);

        dragAndDrop.setThreshold(3);
        dragAndDrop.on('dragStart', e => this.tools.dragStart(e));
        dragAndDrop.on('drag', e => this.tools.drag(e));
        dragAndDrop.on('dragStop', e => this.tools.dragStop(e));

        this.element.on('mousewheel', (e) => {
            this.tools.mouseWheel(e, e.deltaY);
            e.preventDefault();
        });

        this.element.on('mousedown', (e) => {
            this.tools.mouseDown(e);
            Logger.debug('canvas.mousedown()');
            if (!e.isDefaultPrevented()) this.focus();
        });

        this.element.on('click', (e) => {
            Logger.debug('canvas.click()');
            this.tools.click(e);
            if (!e.isDefaultPrevented()) this.focus();
        });

        this.element.on('dblclick', (e) => {
            Logger.debug('canvas.dblclick()');
            this.tools.doubleClick(e);
            if (!e.isDefaultPrevented()) this.focus();
        });

        this.element.on('mousemove', ({ pageX, pageY }) => {
            const point = this.getPointByPixels(pageX, pageY);
            this.tools.mousemove(point);
        });
    }

    initContextMenu() {
        // We need to delay the creation to ensure that the container is build and we can access the clipboard
        requestAnimationFrame(() => {
            const overlay = new ContextMenuOverlay(
                this,
                this.history,
                container.get('editor.clipboard'),
                this.selection,
                container.get('canvas.itemOverlappingCollisionChecker'),
            );
            overlay.init();
        });
    }

    zoomIn(offset) {
        this.changeZoom(offset || 0.1);
    }

    zoomOut(offset) {
        this.changeZoom(-1 * (offset || 0.1));
    }

    changeZoom(offset) {
        const { zoom } = this.options;

        const centerPositionBeforeZoom = this.getBounds().getCenter();
        this.setZoom(zoom + offset);
        const centerPositionAfterZoom = this.getBounds().getCenter();

        this.fixPan(centerPositionBeforeZoom, centerPositionAfterZoom);
    }

    /**
     * Fix pan settings so a specific focus point on the canvas
     * is displayed at the same position.
     *
     * @param {Point} oldPoint old position of focus point
     * @param {Point} newPoint new position of focus point
     */
    fixPan(oldPoint, newPoint) {
        const distance = new Distance(oldPoint, newPoint);

        if (distance.getDistance() === 0) {
            return;
        }

        // set new panning
        this.setPan(
            this.options.panX + distance.point.x,
            this.options.panY + distance.point.y,
        );
    }

    /**
     * Validates a given pan value to be in range between canvas and foam.
     * The min/max fixed pan value will be returned. This method can be used
     * to validate height and width.
     *
     * @private
     * @param {number} value Pan value to be isValid
     * @param {number} canvas Width or height of canvas bounds
     * @param {number} foam Width or height of foam bounds
     * @param {number} zero
     * @return {number} Fixed pan value
     */
    ensurePanValue(value, canvas, foam, zero = 0) {
        const foamFitsToCanvas = canvas > foam;
        const halfDifference = (canvas - foam) / 2;
        const min = foamFitsToCanvas ? zero + halfDifference : (zero + canvas) - foam;
        const max = foamFitsToCanvas ? (zero + canvas) - foam - halfDifference : zero;
        const result = Math.max(Math.min(max, value), min);

        return result;
    }

    /**
     * Set pan
     *
     * @param {number} panX Pan X value
     * @param {number} panY Pan Y value
     */
    setPan(panX, panY) {
        if (this.options.panX === panX && this.options.panY === panY) {
            return;
        }

        const canvasBounds = this.getBounds();
        const fullBounds = this._getFullBounds();

        this.options.panX = this.ensurePanValue(
            panX,
            canvasBounds.getWidth(),
            fullBounds.getWidth(),
            -fullBounds.topLeft.x,
        );

        this.options.panY = this.ensurePanValue(
            panY,
            canvasBounds.getHeight(),
            fullBounds.getHeight(),
            -fullBounds.topLeft.y,
        );

        this.trigger('panchange', this, this.options.panX, this.options.panY);
    }

    setRawPan(panX, panY) {
        if (this.options.panX === panX && this.options.panY === panY) {
            return;
        }

        this.options.panX = panX;
        this.options.panY = panY;

        this.trigger('panchange', this, this.options.panX, this.options.panY);
    }

    /**
     * Adds the rotate handle for the selected item
     */
    showRotateHandle() {
        const item = this.selection.first();

        // Ensure that there is no other rotate handle
        this.hideRotateHandle();

        if (item instanceof TablelegItem || item instanceof VentilationgrillItem) {
            this.rotateHandle = new RotateHandleTabletop(item, this);
        } else {
            this.rotateHandle = new RotateHandle(item, this);
        }
    }

    /**
     * Removed the current rotate handle
     */
    hideRotateHandle() {
        if (this.rotateHandle) {
            this.rotateHandle.remove();
        }
        this.rotateHandle = null;
    }

    /**
     * Adds the resize handle for the selected item
     */
    showResizeHandle() {
        const item = this.selection.first();

        // Ensure that there is no other resize handle
        this.hideResizeHandle();

        if (typeof item.initializeHandles === 'function') {
            this.resizeHandle = item.initializeHandles(this);
        }
    }

    /**
     * Removed the current resize handle
     */
    hideResizeHandle() {
        if (this.resizeHandle) {
            this.resizeHandle.remove();
        }
        this.resizeHandle = null;
    }
}

export default SvgCanvas;
namespace('Ofcs.Editor.Svg.Canvas');
window.Ofcs.Editor.Svg.Canvas = SvgCanvas;
